<template>
  <div>
    <!-- Opt-in Banner (invite users to try new version) -->
    <div 
      v-if="showBanner && !isEnabled" 
      :class="['feature-banner', 'opt-in-banner']"
    >
      <div class="banner-content">
        <div class="banner-icon">
          <i :class="optInIcon"></i>
        </div>
        <div class="banner-text">
          <strong>{{ optInTitle }}</strong>
          <span class="banner-description">
            {{ optInDescription }}
          </span>
        </div>
        <div class="banner-actions">
          <button 
            class="btn btn-sm btn-light mr-2" 
            @click="enableFeature"
          >
            <i class="fas fa-rocket"></i> {{ optInButtonText }}
          </button>
          <button 
            class="btn btn-sm btn-link text-white" 
            @click="dismissBanner"
          >
            {{ dismissButtonText }}
          </button>
        </div>
        <button 
          class="banner-close" 
          @click="dismissBanner"
          aria-label="Close banner"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Feedback Banner (option to revert) -->
    <div 
      v-if="showFeedback && isEnabled" 
      :class="['feature-banner', 'feedback-banner']"
    >
      <div class="banner-content">
        <div class="banner-icon">
          <i :class="feedbackIcon"></i>
        </div>
        <div class="banner-text">
          <strong>{{ feedbackTitle }}</strong>
          <span class="banner-description">
            {{ feedbackDescription }}
          </span>
        </div>
        <div class="banner-actions">
          <button 
            class="btn btn-sm btn-outline-secondary mr-2" 
            @click="disableFeature"
          >
            <i class="fas fa-undo"></i> {{ revertButtonText }}
          </button>
          <button 
            class="btn btn-sm btn-link text-muted" 
            @click="dismissFeedback"
          >
            {{ dismissButtonText }}
          </button>
        </div>
        <button 
          class="banner-close" 
          @click="dismissFeedback"
          aria-label="Close banner"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getFeatureFlag, setFeatureFlag } from '../../utils/featureFlags';

export default {
  name: 'FeatureOptInBanner',
  props: {
    // Feature flag key
    featureName: {
      type: String,
      required: true
    },
    // Opt-in banner content
    optInTitle: {
      type: String,
      default: 'Try our new feature!'
    },
    optInDescription: {
      type: String,
      default: 'Experience an improved interface with better functionality.'
    },
    optInButtonText: {
      type: String,
      default: 'Try it now'
    },
    optInIcon: {
      type: String,
      default: 'fas fa-sparkles'
    },
    // Feedback banner content
    feedbackTitle: {
      type: String,
      default: 'You\'re using the new version!'
    },
    feedbackDescription: {
      type: String,
      default: 'Want to go back to the classic version?'
    },
    revertButtonText: {
      type: String,
      default: 'Use classic version'
    },
    feedbackIcon: {
      type: String,
      default: 'fas fa-lightbulb'
    },
    // Common
    dismissButtonText: {
      type: String,
      default: 'Maybe later'
    },
    // Success/info messages
    enabledMessage: {
      type: String,
      default: 'Welcome to the new version!'
    },
    disabledMessage: {
      type: String,
      default: 'Switched to classic version'
    },
    // Persistence options
    persistDismissal: {
      type: Boolean,
      default: false // false = session only, true = permanent
    }
  },
  data() {
    return {
      showBanner: true,
      showFeedback: true,
    };
  },
  computed: {
    isEnabled() {
      return getFeatureFlag(this.featureName, false);
    },
    optInDismissalKey() {
      return `${this.featureName}_optInDismissed`;
    },
    feedbackDismissalKey() {
      return `${this.featureName}_feedbackDismissed`;
    }
  },
  methods: {
    enableFeature() {
      setFeatureFlag(this.featureName, true);
      this.dismissBanner();
      
      // Show success notification
      this.showNotification('success', this.enabledMessage);
      
      // Dispatch custom event to notify parent wrapper
      window.dispatchEvent(new CustomEvent('appointmentVersionChanged'));
      
      // Emit event for parent to react
      this.$emit('feature-enabled');
      
      // Force reload immediately
      window.location.reload();
    },
    disableFeature() {
      setFeatureFlag(this.featureName, false);
      this.dismissFeedback();
      
      // Show notification
      this.showNotification('info', this.disabledMessage);
      
      // Dispatch custom event to notify parent wrapper
      window.dispatchEvent(new CustomEvent('appointmentVersionChanged'));
      
      // Emit event for parent to react
      this.$emit('feature-disabled');
      
      // Force reload immediately
      window.location.reload();
    },
    dismissBanner() {
      this.showBanner = false;
      const storage = this.persistDismissal ? localStorage : sessionStorage;
      storage.setItem(this.optInDismissalKey, 'true');
      
      this.$emit('banner-dismissed');
    },
    dismissFeedback() {
      this.showFeedback = false;
      const storage = this.persistDismissal ? localStorage : sessionStorage;
      storage.setItem(this.feedbackDismissalKey, 'true');
      
      this.$emit('feedback-dismissed');
    },
    showNotification(type, message) {
      // Use SweetAlert if available
      if (this.$swal) {
        this.$swal({
          icon: type,
          title: message,
          timer: 3000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false
        });
      } else if (window.Swal) {
        window.Swal.fire({
          icon: type,
          title: message,
          timer: 3000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false
        });
      }
    },
    checkBannerVisibility() {
      const storage = this.persistDismissal ? localStorage : sessionStorage;
      
      const optInDismissed = storage.getItem(this.optInDismissalKey);
      const feedbackDismissed = storage.getItem(this.feedbackDismissalKey);
      
      this.showBanner = !optInDismissed;
      this.showFeedback = !feedbackDismissed;
    }
  },
  mounted() {
    this.checkBannerVisibility();
  }
};
</script>

<style scoped>
/* Base Banner Styles */
.feature-banner {
  padding: 16px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: slideDown 0.4s ease-out;
  margin-bottom: 20px;
  border-radius: 8px;
}

/* Opt-in Banner (Purple gradient) */
.opt-in-banner {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

/* Feedback Banner (Neutral blue-gray gradient) */
.feedback-banner {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  padding-right: 32px; /* Make room for close button */
}

.banner-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.banner-icon i {
  animation: pulse 2s ease-in-out infinite;
}

.banner-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.banner-text strong {
  font-size: 16px;
  font-weight: 600;
}

.banner-description {
  font-size: 14px;
  opacity: 0.95;
}

.banner-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-right: 8px; /* Add space before close button */
}

.banner-close {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  opacity: 0.8;
  transition: all 0.2s;
  line-height: 1;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.banner-close:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.2);
}

.btn-light:hover {
  background-color: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-outline-secondary {
  background-color: white;
  border-color: white;
  color: #6c757d;
}

.btn-outline-secondary:hover {
  background-color: #f8f9fa;
  border-color: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Animations */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .banner-content {
    flex-wrap: wrap;
    padding-right: 40px; /* More space for close button */
  }
  
  .banner-icon {
    font-size: 24px;
  }
  
  .banner-actions {
    width: 100%;
    margin-top: 8px;
    margin-right: 0;
  }
  
  .banner-description {
    font-size: 13px;
  }
  
  .banner-close {
    top: 8px;
    right: 8px;
    transform: none;
  }
}

@media (max-width: 576px) {
  .feature-banner {
    padding: 12px 16px;
    padding-right: 48px; /* Extra space for close button on mobile */
    border-radius: 0;
    margin-left: -15px;
    margin-right: -15px;
    margin-bottom: 15px;
  }
  
  .banner-content {
    padding-right: 0;
  }
  
  .banner-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .banner-actions button {
    width: 100%;
    margin: 4px 0 !important;
  }
  
  .banner-close {
    right: 12px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/shared/FeatureOptInBanner.vue